import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  Tooltip,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import clientService from 'src/clientService';
import { AcousticIndexEnum, ChartRequest, ChartResponse, FileAdminDetailWithUser } from 'src/api/Api';
import SpeciesActivityCard from 'src/view/tool/bioacoustic/components/speciesCatalog/SpeciesActivityCard';
import AcousticIndicesCard from 'src/view/tool/bioacoustic/components/speciesCatalog/AcousticIndicesCard';
import { ViewMode, useComparedDeployments } from 'src/view/tool/bioacoustic/components/speciesCatalog/DeploymentControls';
import { speciesTrendsOf } from 'src/view/tool/bioacoustic/components/speciesCatalog/chartTheme';

interface BioacousticAnalysisProps {
  deploymentId?: string;
  siteName?: string;
  deploymentName?: string;
  deploymentsList?: Array<{
    deployment_id: string;
    deployment_name: string;
    site_name?: string;
    [key: string]: unknown;
  }>;
  onDeploymentSelect?: (id: string, name: string, siteName?: string) => void;
  onUploadClick?: () => void;
}

const describeChartError = (err: unknown): { noData: boolean; message: string } => {
  const e = (err ?? {}) as { status?: number; message?: string; error?: string | { message?: string } };
  const message = (typeof e.error === 'object' ? e.error?.message : e.error) || e.message || '';
  const noData = e.status === 404 && !/not authori[sz]ed/i.test(message);
  if (e.status === 401 || /refresh token/i.test(message)) {
    return { noData: false, message: 'Your session has expired. Sign in again to load the charts.' };
  }
  return { noData, message: message || 'Could not load chart data.' };
};

const BioacousticAnalysis: React.FC<BioacousticAnalysisProps> = ({
  deploymentId,
  siteName: _siteName,
  deploymentName,
  deploymentsList = [],
  onDeploymentSelect,
  onUploadClick,
}) => {
  const currentDeploymentId = deploymentId || (deploymentsList.length > 0 ? deploymentsList[0].deployment_id : undefined);
  const currentDeploymentObj = deploymentsList.find((d) => d.deployment_id === currentDeploymentId);
  const currentDeploymentName = deploymentName || currentDeploymentObj?.deployment_name || 'Selected Station';

  const [activityMode, setActivityMode] = useState<ViewMode>('single');
  const [indicesMode, setIndicesMode] = useState<ViewMode>('single');
  const anyMulti = activityMode === 'multi' || indicesMode === 'multi';
  const multiDisabledReason = deploymentsList.length < 2 ? 'Needs at least two deployments to compare' : undefined;

  const {
    compared,
    add: addComparedDeployment,
    remove: removeComparedDeployment,
  } = useComparedDeployments(currentDeploymentId, deploymentsList);

  const requestedIds = useMemo(() => {
    if (!currentDeploymentId) return [];
    return anyMulti && compared.length > 0 ? compared.map((d) => d.id) : [currentDeploymentId];
  }, [anyMulti, compared, currentDeploymentId]);
  const requestKey = requestedIds.join(',');

  const [chartData, setChartData] = useState<ChartResponse | null>(null);
  const [chartLoading, setChartLoading] = useState<boolean>(false);
  const [chartError, setChartError] = useState<string | null>(null);
  const latestRequest = useRef(0);

  const [fileStats, setFileStats] = useState<{ processed: number; total: number } | null>(null);
  const [filesLoading, setFilesLoading] = useState<boolean>(false);

  const fetchDeploymentFiles = useCallback(async (depId?: string) => {
    if (!depId) {
      setFileStats(null);
      return;
    }
    setFilesLoading(true);
    try {
      const res = await clientService.api.getFilesForDeploymentApiV1AdminFilesDeploymentIdGet(depId, {
        secure: true,
      });
      const files: FileAdminDetailWithUser[] = res?.data ?? [];
      const total = files.length;
      const processed = files.filter(
        (f) =>
          f.status?.toUpperCase() === 'COMPLETED' ||
          (Array.isArray(f.inferences) && f.inferences.length > 0)
      ).length;
      setFileStats({ processed, total });
    } catch (err: unknown) {
      console.warn('Could not fetch deployment files for count:', depId, err);
      setFileStats(null);
    } finally {
      setFilesLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDeploymentFiles(currentDeploymentId);
  }, [currentDeploymentId, fetchDeploymentFiles]);

  const fetchBioInsituCharts = useCallback(async (depIds: string[]) => {
    if (depIds.length === 0) {
      setChartData(null);
      setChartError(null);
      setChartLoading(false);
      return;
    }
    const requestId = ++latestRequest.current;
    setChartLoading(true);
    setChartError(null);
    try {
      const req: ChartRequest = {
        deploymentIds: depIds,
        index: [
          AcousticIndexEnum.Aci,
          AcousticIndexEnum.Bi,
          AcousticIndexEnum.Adi,
          AcousticIndexEnum.Aei,
          AcousticIndexEnum.Ndsi,
        ],
      };
      const res = await clientService.api.createDeploymentChartsApiV1DeploymentsBioInsituChartPost(req);
      if (requestId === latestRequest.current) setChartData(res?.data ?? null);
    } catch (err: unknown) {
      if (requestId !== latestRequest.current) return;
      setChartData(null);
      const { noData, message } = describeChartError(err);
      if (!noData) {
        console.warn('Could not fetch bio-insitu chart data for deployments:', depIds, err);
        setChartError(message);
      }
    } finally {
      if (requestId === latestRequest.current) setChartLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBioInsituCharts(requestKey ? requestKey.split(',') : []);
  }, [requestKey, fetchBioInsituCharts]);

  const comparisons = useMemo(
    () => new Map((chartData?.comparison ?? []).map((c) => [c.deployment_id, c])),
    [chartData],
  );
  const primaryComparison = currentDeploymentId ? comparisons.get(currentDeploymentId) : undefined;

  const timeSlots = useMemo(() => {
    if (chartData?.time_slots && chartData.time_slots.length > 0) return chartData.time_slots;
    if (primaryComparison?.activity_trend?.time_slots?.length) return primaryComparison.activity_trend.time_slots;
    const slots: string[] = [];
    for (let h = 0; h < 24; h++) {
      const hh = h.toString().padStart(2, '0');
      slots.push(`${hh}:00`, `${hh}:30`);
    }
    return slots;
  }, [chartData, primaryComparison]);

  const detectedSpeciesList = useMemo(() => speciesTrendsOf(primaryComparison), [primaryComparison]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, width: '100%', pb: 4 }}>
      {/* Section Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography sx={{ fontSize: '10px', color: '#10B981', letterSpacing: '1.5px', textTransform: 'uppercase', fontWeight: 800, mb: 0.5 }}>
            Station Analysis & Bio-Insitu Report
          </Typography>
          <Typography sx={{ fontSize: '12px', color: '#888', mt: 0.5 }}>
            Diurnal acoustic activity, soundscape complexity indices, and deployment-level trend analysis.
          </Typography>
        </Box>

        {/* Station / Deployment Selector Toolbar */}
        {deploymentsList.length > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Select
              value={currentDeploymentId || ''}
              onChange={(e) => {
                const selected = deploymentsList.find((d) => d.deployment_id === e.target.value);
                if (selected && onDeploymentSelect) {
                  onDeploymentSelect(selected.deployment_id, selected.deployment_name, selected.site_name || '');
                }
              }}
              size="small"
              sx={{
                bgcolor: '#111',
                color: '#10B981',
                fontWeight: 600,
                fontSize: '12px',
                border: '1px solid #10B981',
                borderRadius: '6px',
                height: '32px',
                minWidth: '180px',
                '& .MuiSelect-select': { py: 0, px: 1.5, display: 'flex', alignItems: 'center' },
                '& .MuiSvgIcon-root': { color: '#10B981' },
                '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    bgcolor: '#111',
                    border: '1px solid #222',
                    color: '#fff',
                    maxHeight: 280,
                    '& .MuiMenuItem-root': {
                      fontSize: '12px',
                      '&.Mui-selected': { bgcolor: 'rgba(16,185,129,0.15)', color: '#10B981' },
                    },
                  },
                },
              }}
            >
              {deploymentsList.map((d) => (
                <MenuItem key={d.deployment_id} value={d.deployment_id}>
                  {d.deployment_name} {d.site_name ? `(${d.site_name})` : ''}
                </MenuItem>
              ))}
            </Select>
            <Tooltip title="Refresh Bio-Insitu Charts" arrow>
              <Button
                variant="outlined"
                size="small"
                onClick={() => {
                  fetchBioInsituCharts(requestedIds);
                  fetchDeploymentFiles(currentDeploymentId);
                }}
                sx={{
                  minWidth: '32px',
                  width: '32px',
                  height: '32px',
                  p: 0,
                  borderColor: '#282828',
                  color: '#888',
                  bgcolor: 'rgba(255,255,255,0.02)',
                  borderRadius: '6px',
                  '&:hover': { borderColor: '#10B981', color: '#10B981', bgcolor: 'rgba(16,185,129,0.1)' },
                }}
              >
                <RefreshIcon sx={{ fontSize: 16 }} />
              </Button>
            </Tooltip>
          </Box>
        )}
      </Box>

      {/* Top Three Summary Chips */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2 }}>
        {[
          {
            label: 'Total Species',
            value: chartLoading ? '...' : detectedSpeciesList.length,
            sub: detectedSpeciesList.length > 0 ? 'Detected via bio-insitu data' : 'No species detected',
            color: '#10B981',
          },
          {
            label: 'Total Detections',
            value: chartLoading
              ? '...'
              : detectedSpeciesList.reduce((acc, s) => acc + s.total, 0),
            sub: 'Across recorded audio files',
            color: '#10B981',
          },
          {
            label: 'Files Processed',
            value: filesLoading ? '...' : fileStats ? fileStats.processed : 0,
            sub:
              fileStats && fileStats.total === 0
                ? 'No audio files uploaded'
                : 'In-situ audio inference cataloged',
            color: '#10B981',
          },
        ].map((stat) => (
          <Box
            key={stat.label}
            sx={{
              background: 'linear-gradient(180deg, rgba(18, 24, 21, 0.85) 0%, rgba(10, 14, 12, 0.95) 100%)',
              border: '1px solid #1E2E25',
              borderRadius: '12px',
              p: '16px 18px',
              transition: 'border-color 0.2s',
              '&:hover': { borderColor: 'rgba(16,185,129,0.4)' },
            }}
          >
            <Typography sx={{ fontSize: '10px', color: '#888888', letterSpacing: '0.8px', mb: 0.5, textTransform: 'uppercase', fontWeight: 700 }}>
              {stat.label}
            </Typography>
            <Typography sx={{ fontSize: '28px', fontWeight: 800, color: stat.color, lineHeight: 1 }}>
              {stat.value}
            </Typography>
            <Typography sx={{ fontSize: '10.5px', color: '#6B7280', mt: 0.75 }}>{stat.sub}</Typography>
          </Box>
        ))}
      </Box>

      {/* Chart 1: Species Activity Card */}
      <SpeciesActivityCard
        mode={activityMode}
        onModeChange={setActivityMode}
        multiDisabledReason={multiDisabledReason}
        primaryId={currentDeploymentId}
        primaryName={currentDeploymentName}
        compared={compared}
        deploymentsList={deploymentsList}
        onAddDeployment={addComparedDeployment}
        onRemoveDeployment={removeComparedDeployment}
        comparisons={comparisons}
        timeSlots={timeSlots}
        loading={chartLoading}
        error={chartError}
        onRetry={() => fetchBioInsituCharts(requestedIds)}
        onUploadClick={onUploadClick}
      />

      {/* Chart 2: Acoustic Indices Card */}
      <AcousticIndicesCard
        mode={indicesMode}
        onModeChange={setIndicesMode}
        multiDisabledReason={multiDisabledReason}
        primaryId={currentDeploymentId}
        primaryName={currentDeploymentName}
        compared={compared}
        deploymentsList={deploymentsList}
        onAddDeployment={addComparedDeployment}
        onRemoveDeployment={removeComparedDeployment}
        comparisons={comparisons}
        timeSlots={timeSlots}
        loading={chartLoading}
        error={chartError}
        onRetry={() => fetchBioInsituCharts(requestedIds)}
        onUploadClick={onUploadClick}
      />
    </Box>
  );
};

export default BioacousticAnalysis;
